class ShappMclDiscountCodeManager extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('.shapp-mcl-discount-code-manager__form');
    this.input = this.querySelector('.shapp-mcl-discount-code-manager__input');
    this.applyButton = this.querySelector('.shapp-mcl-discount-code-manager__apply-btn');
    this.errorElement = this.querySelector('.shapp-mcl-discount-code-manager__error');

    this.#bindEvents();
  }

  #bindEvents() {
    this.form.addEventListener('submit', this.#handleSubmit.bind(this));
    this.input.addEventListener('input', this.#handleInput.bind(this));

    // Handle remove discount buttons
    this.addEventListener('click', this.#handleRemoveDiscount.bind(this));
  }

  #handleInput() {
    this.#hideError();
    this.#enableApplyButton();
  }

  async #handleSubmit(event) {
    event.preventDefault();

    const discountCode = this.input.value.trim();
    if (!discountCode) return;

    this.#hideError();

    try {
      const response = await this.#applyDiscountCode(discountCode);
      const responseText = await response.text();

      if (response.ok) {
        const parsedState = JSON.parse(responseText);

        // Check for errors in the response
        if (parsedState.errors || parsedState.error) {
          this.#showError();
        } else {
          // Check if we have any valid discount codes applied
          let discountCodes = [];

          if (parsedState.discount_codes && Array.isArray(parsedState.discount_codes)) {
            discountCodes = parsedState.discount_codes
              .filter(d => d.applicable)
              .map(d => d.code);
          }

          // Check if we have any discount codes (the new one might have replaced others)
          const hasAnyDiscounts = discountCodes.length > 0;

          if (hasAnyDiscounts) {
            await this.#handleSuccess();
          } else {
            this.#showError();
          }
        }
      } else {
        this.#showError();
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      this.#showError();
    }
  }

  async #handleRemoveDiscount(event) {
    const removeButton = event.target.closest('.shapp-mcl-discount-chip__remove');
    if (!removeButton) return;

    event.preventDefault();

    const discountCode = removeButton.dataset.discountCode;
    if (!discountCode) return;

    try {
      const response = await this.#removeDiscountCode(discountCode);
      const responseText = await response.text();

      if (response.ok) {
        await this.#refreshCartDrawer();
      } else {
        console.error('Error removing discount code');
      }
    } catch (error) {
      console.error('Error removing discount:', error);
    }
  }

  async #applyDiscountCode(code) {
    // First, get current cart to see existing discounts
    const cartResponse = await fetch(window.routes.cart_url + '.js');
    const currentCart = await cartResponse.json();

    // Get existing discount codes - use discount_codes array
    let existingDiscounts = [];

    if (currentCart.discount_codes && Array.isArray(currentCart.discount_codes)) {
      existingDiscounts = currentCart.discount_codes
        .filter(discount => discount.applicable)
        .map(discount => discount.code);
    }

    // Add the new discount code to the array (if not already present)
    if (!existingDiscounts.includes(code)) {
      existingDiscounts.push(code);
    }

    const discountString = existingDiscounts.join(',');

    const body = JSON.stringify({
      discount: discountString
    });

    return fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    });
  }

  async #removeDiscountCode(codeToRemove) {
    // Get current cart to see existing discounts
    const cartResponse = await fetch(window.routes.cart_url + '.js');
    const currentCart = await cartResponse.json();

    // Get existing discount codes, filtering out the one to remove
    let remainingDiscounts = [];

    if (currentCart.discount_codes && Array.isArray(currentCart.discount_codes)) {
      remainingDiscounts = currentCart.discount_codes
        .filter(discount => discount.applicable && discount.code.toLowerCase() !== codeToRemove.toLowerCase())
        .map(discount => discount.code);
    }

    const body = JSON.stringify({
      discount: remainingDiscounts.join(',')
    });

    return fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    });
  }

  #getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
    ];
  }

  async #handleSuccess() {
    this.input.value = '';

    // Manually refresh the cart drawer
    await this.#refreshCartDrawer();
  }

  async #refreshCartDrawer() {
    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Update cart drawer content
      const cartDrawer = document.querySelector('cart-drawer .drawer__inner');
      const newContent = html.querySelector('.drawer__inner');

      if (cartDrawer && newContent) {
        cartDrawer.innerHTML = newContent.innerHTML;
      }

      // Update cart icon
      const cartIconResponse = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
      const cartIconText = await cartIconResponse.text();
      const cartIconHtml = new DOMParser().parseFromString(cartIconText, 'text/html');
      const cartIcon = document.querySelector('#cart-icon-bubble');
      const newCartIcon = cartIconHtml.querySelector('#cart-icon-bubble');

      if (cartIcon && newCartIcon) {
        cartIcon.innerHTML = newCartIcon.innerHTML;
      }

      // Publish cart update event
      if (typeof publish !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shapp-mcl-discount-code-manager' });
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  async #updateCartDrawerWithState(parsedState) {
    try {
      this.#getSectionsToRender().forEach((section) => {
        const targetElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (targetElement && parsedState.sections[section.section]) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = parsedState.sections[section.section];
          const sourceElement = section.selector
            ? tempDiv.querySelector(section.selector)
            : tempDiv.querySelector('.shopify-section');

          if (sourceElement) {
            if (section.id === 'cart-drawer') {
              const cartDrawer = document.querySelector('cart-drawer .drawer__inner');
              if (cartDrawer) {
                cartDrawer.innerHTML = sourceElement.innerHTML;
              }
            } else {
              targetElement.innerHTML = sourceElement.innerHTML;
            }
          }
        }
      });

      // Publish cart update event
      if (typeof publish !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shapp-mcl-discount-code-manager' });
      }
    } catch (error) {
      console.error('Error updating cart drawer:', error);
      // Fallback to full page reload
      window.location.reload();
    }
  }

  #enableApplyButton() {
    this.applyButton.disabled = !this.input.value.trim();
  }

  #showError() {
    if (this.errorElement) {
      this.errorElement.style.display = 'block';
    } else {
      this.#createErrorElement();
    }
  }

  #createErrorElement() {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'shapp-mcl-discount-code-manager__error';
    errorDiv.setAttribute('role', 'alert');
    errorDiv.innerHTML = `<span class="shapp-mcl-discount-code-manager__error-text">${window.cartStrings.discount_error || 'Invalid discount code'}</span>`;

    // Insert after the input wrapper
    const inputWrapper = this.querySelector('.shapp-mcl-discount-code-manager__input-wrapper');
    if (inputWrapper) {
      inputWrapper.insertAdjacentElement('afterend', errorDiv);
      this.errorElement = errorDiv;
    }
  }

  #hideError() {
    if (this.errorElement) {
      this.errorElement.style.display = 'none';
    }
  }
}

customElements.define('shapp-mcl-discount-code-manager', ShappMclDiscountCodeManager);
